import React, { useEffect, useState } from "react";
import { fetchCategories, exportCategory, importCategory, transferCategories } from "../api/CategoryAPI";
import {
    getAllUnits, getAllUnitSystems, getAllUnitDimensions,
    exportUnit, importUnit,
    exportUnitSystem, importUnitSystem,
    exportUnitDimension, importUnitDimension,
    transferUnits, transferUnitSystems, transferUnitDimensions,
} from "../api/UnitAPI";
import { logApiError, getUserMessage } from "../api/apiError";

type ResourceType = "CATEGORY" | "UNIT" | "UNIT_SYSTEM" | "UNIT_DIMENSION";

interface ListItem { id: string; name: string; }

interface ExportFile {
    resourceType: ResourceType;
    exportedAt: string;
    items: unknown[];
}

// A complete unit-data bundle: every dimension, system and unit (even ones not
// referenced by any unit), imported in dependency order on the way back in.
interface UnitBundleFile {
    kind: "UNIT_BUNDLE";
    exportedAt: string;
    unitDimensions: unknown[];
    unitSystems: unknown[];
    units: unknown[];
}

type TransferResult = { succeeded: string[]; failed: { categoryName: string; reason: string }[] };

const RESOURCE_LABELS: Record<ResourceType, string> = {
    CATEGORY: "Categories",
    UNIT: "Units",
    UNIT_SYSTEM: "Unit Systems",
    UNIT_DIMENSION: "Unit Dimensions",
};

function downloadJson(filename: string, data: unknown): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

interface ImportExportPanelProps { token: string; }

const ImportExportPanel: React.FC<ImportExportPanelProps> = ({ token }) => {
    const [tab, setTab] = useState<"export" | "import">("export");
    return (
        <div className="section-box">
            <h3 style={{ marginBottom: "1.5rem" }}>Import / Export</h3>
            <div className="button-group" style={{ marginBottom: "1.5rem" }}>
                <button className={tab === "export" ? "" : "btn-secondary"} onClick={() => setTab("export")}>Export</button>
                <button className={tab === "import" ? "" : "btn-secondary"} onClick={() => setTab("import")}>Import</button>
            </div>
            {tab === "export" ? <ExportSection token={token} /> : <ImportSection token={token} />}
        </div>
    );
};

// ── Export ───────────────────────────────────────────────────────────────────

interface TransferResultRow { name: string; success: boolean; reason?: string; }

const ExportSection: React.FC<{ token: string }> = ({ token }) => {
    const [targetUrl, setTargetUrl]       = useState("");
    const [targetToken, setTargetToken]   = useState("");
    const [showToken, setShowToken]       = useState(false);
    const [resourceType, setResourceType] = useState<ResourceType>("CATEGORY");
    const [items, setItems]               = useState<ListItem[]>([]);
    const [selected, setSelected]         = useState<Set<string>>(new Set());
    const [loadingList, setLoadingList]   = useState(false);
    const [transferring, setTransferring] = useState(false);
    const [downloading, setDownloading]   = useState(false);
    const [bundling, setBundling]         = useState(false);
    const [bundleError, setBundleError]   = useState<string | null>(null);
    const [results, setResults]           = useState<TransferResultRow[] | null>(null);
    const [error, setError]               = useState<string | null>(null);

    useEffect(() => {
        setSelected(new Set());
        setResults(null);
        setError(null);
        loadList();
    }, [resourceType]);

    const loadList = async () => {
        setLoadingList(true);
        setError(null);
        try {
            let loaded: ListItem[] = [];
            if (resourceType === "CATEGORY")       { const r = await fetchCategories(token);      loaded = r.map(x => ({ id: x.id,   name: x.name })); }
            else if (resourceType === "UNIT")       { const r = await getAllUnits(token);           loaded = r.map(x => ({ id: x.id,   name: x.name })); }
            else if (resourceType === "UNIT_SYSTEM") { const r = await getAllUnitSystems(token);   loaded = r.map(x => ({ id: x.id,   name: x.name })); }
            else                                   { const r = await getAllUnitDimensions(token);  loaded = r.map(x => ({ id: x.id,   name: x.name })); }
            setItems(loaded);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoadingList(false);
        }
    };

    const allSelected = items.length > 0 && selected.size === items.length;
    const toggleAll   = () => setSelected(allSelected ? new Set() : new Set(items.map(i => i.id)));
    const toggleItem  = (id: string) => setSelected(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const exportOne = (id: string): Promise<unknown> => {
        if (resourceType === "CATEGORY")        return exportCategory(token, id);
        if (resourceType === "UNIT")            return exportUnit(token, id);
        if (resourceType === "UNIT_SYSTEM")     return exportUnitSystem(token, id);
        return exportUnitDimension(token, id);
    };

    const handleDownload = async () => {
        setDownloading(true);
        setResults(null);
        setError(null);
        try {
            const chosen = items.filter(i => selected.has(i.id));
            const exported = await Promise.all(chosen.map(i => exportOne(i.id)));
            const file: ExportFile = {
                resourceType,
                exportedAt: new Date().toISOString(),
                items: exported,
            };
            downloadJson(`${resourceType.toLowerCase()}-export-${new Date().toISOString().slice(0, 10)}.json`, file);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadBundle = async () => {
        setBundling(true);
        setBundleError(null);
        try {
            const [dims, systems, units] = await Promise.all([
                getAllUnitDimensions(token),
                getAllUnitSystems(token),
                getAllUnits(token),
            ]);
            const [unitDimensions, unitSystems, exportedUnits] = await Promise.all([
                Promise.all(dims.map(d => exportUnitDimension(token, d.id))),
                Promise.all(systems.map(s => exportUnitSystem(token, s.id))),
                Promise.all(units.map(u => exportUnit(token, u.id))),
            ]);
            const bundle: UnitBundleFile = {
                kind: "UNIT_BUNDLE",
                exportedAt: new Date().toISOString(),
                unitDimensions,
                unitSystems,
                units: exportedUnits,
            };
            downloadJson(`unit-data-bundle-${new Date().toISOString().slice(0, 10)}.json`, bundle);
        } catch (err) {
            logApiError(err);
            setBundleError(getUserMessage(err));
        } finally {
            setBundling(false);
        }
    };

    const handleTransfer = async () => {
        setTransferring(true);
        setResults(null);
        setError(null);

        const ids = items.filter(i => selected.has(i.id)).map(i => i.id);
        try {
            let result: TransferResult;
            if (resourceType === "CATEGORY")        result = await transferCategories(token, ids, targetUrl, targetToken);
            else if (resourceType === "UNIT")        result = await transferUnits(token, ids, targetUrl, targetToken);
            else if (resourceType === "UNIT_SYSTEM") result = await transferUnitSystems(token, ids, targetUrl, targetToken);
            else                                     result = await transferUnitDimensions(token, ids, targetUrl, targetToken);

            const rows: TransferResultRow[] = [
                ...result.succeeded.map(name => ({ name, success: true })),
                ...result.failed.map(f => ({ name: f.categoryName, success: false, reason: f.reason })),
            ];
            setResults(rows);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setTransferring(false);
        }
    };

    return (
        <div>
            {/* Full unit-data bundle */}
            <div style={{ maxWidth: 480, marginBottom: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-color)" }}>
                <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>All Unit Data</div>
                <div className="item-description" style={{ marginBottom: "0.75rem" }}>
                    Download every unit dimension, unit system and unit (including ones not used
                    by any unit) as a single file. Importing it recreates them in the right order.
                </div>
                <button onClick={handleDownloadBundle} disabled={bundling}>
                    {bundling ? "Preparing…" : "Download All Unit Data"}
                </button>
                {bundleError && <div className="error-message" style={{ marginTop: "0.5rem" }}>{bundleError}</div>}
            </div>

            {/* Target server settings */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxWidth: 480, marginBottom: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-color)" }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Target Server URL</label>
                    <input
                        type="url"
                        value={targetUrl}
                        onChange={e => setTargetUrl(e.target.value)}
                        placeholder="https://prod.example.com"
                        disabled={transferring}
                    />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>JWT Token</label>
                    <input
                        type={showToken ? "text" : "password"}
                        value={targetToken}
                        onChange={e => setTargetToken(e.target.value)}
                        placeholder="Bearer token for the target server"
                        disabled={transferring}
                    />
                </div>
                <div>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", userSelect: "none", fontWeight: "normal" }}>
                        <input type="checkbox" checked={showToken} onChange={e => setShowToken(e.target.checked)} style={{ width: "auto" }} />
                        Show token
                    </label>
                </div>
            </div>

            {/* Resource type tabs */}
            <div className="button-group" style={{ marginBottom: "1rem" }}>
                {(Object.keys(RESOURCE_LABELS) as ResourceType[]).map(rt => (
                    <button key={rt} className={resourceType === rt ? "" : "btn-secondary"}
                        onClick={() => setResourceType(rt)} disabled={loadingList}>
                        {RESOURCE_LABELS[rt]}
                    </button>
                ))}
            </div>

            {/* List */}
            {loadingList ? (
                <div style={{ color: "var(--text-secondary)" }}>Loading…</div>
            ) : items.length === 0 ? (
                <div style={{ color: "var(--text-secondary)" }}>No items found.</div>
            ) : (
                <div style={{
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--radius-md)",
                    overflow: "hidden",
                    maxWidth: 480,
                    marginBottom: "1rem",
                }}>
                    {/* Select all */}
                    <div style={{ padding: "0.5rem 1rem", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border-color)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }} onClick={toggleAll}>
                            <input type="checkbox" checked={allSelected} onChange={toggleAll} onClick={e => e.stopPropagation()} style={{ width: "auto" }} />
                            <span style={{ fontWeight: 500 }}>Select all ({items.length})</span>
                        </div>
                    </div>

                    {/* Rows */}
                    <div style={{ maxHeight: 320, overflowY: "auto" }}>
                        {items.map(item => (
                            <div
                                key={item.id}
                                style={{
                                    display: "flex", alignItems: "center", gap: "0.25rem",
                                    padding: "0.5rem 1rem", cursor: "pointer",
                                    borderBottom: "1px solid var(--border-color)",
                                    background: selected.has(item.id) ? "var(--bg-secondary)" : undefined,
                                }}
                                onClick={() => toggleItem(item.id)}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.has(item.id)}
                                    onChange={() => toggleItem(item.id)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ flexShrink: 0, width: "auto" }}
                                />
                                <span style={{ fontWeight: "normal" }}>{item.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {error && <div className="error-message" style={{ marginBottom: "0.75rem" }}>{error}</div>}

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button
                    onClick={handleDownload}
                    disabled={selected.size === 0 || downloading || transferring || loadingList}
                >
                    {downloading ? "Preparing…" : `Download to File (${selected.size})`}
                </button>
                <button
                    className="btn-secondary"
                    onClick={handleTransfer}
                    disabled={selected.size === 0 || transferring || downloading || loadingList || !targetUrl.trim() || !targetToken.trim()}
                >
                    {transferring ? "Transferring…" : `Transfer to Server (${selected.size})`}
                </button>
            </div>

            {/* Transfer results */}
            {results && results.length > 0 && (
                <div style={{ marginTop: "1rem", maxWidth: 480 }}>
                    {results.map((r, i) => (
                        <div key={i} style={{
                            display: "flex", gap: "0.5rem", alignItems: "baseline",
                            padding: "0.35rem 0", borderBottom: "1px solid var(--border-color)",
                        }}>
                            <span style={{ color: r.success ? "var(--color-success, green)" : "var(--color-error, red)" }}>
                                {r.success ? "✓" : "✗"}
                            </span>
                            <span style={{ fontWeight: 500 }}>{r.name}</span>
                            {r.reason && <span style={{ fontSize: "0.85rem", color: "var(--color-error, red)" }}>— {r.reason}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Import ───────────────────────────────────────────────────────────────────

interface ImportResult { name: string; status: "success" | "error"; message?: string; }

const ImportSection: React.FC<{ token: string }> = ({ token }) => {
    const [parsed, setParsed]       = useState<ExportFile | null>(null);
    const [bundle, setBundle]       = useState<UnitBundleFile | null>(null);
    const [exportedAt, setExportedAt] = useState<string | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [importing, setImporting] = useState(false);
    const [results, setResults]     = useState<ImportResult[]>([]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setResults([]); setParseError(null); setParsed(null); setBundle(null); setExportedAt(null);
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = ev => {
            try {
                const json = JSON.parse(ev.target?.result as string) as any;
                if (json && json.kind === "UNIT_BUNDLE"
                    && Array.isArray(json.unitDimensions) && Array.isArray(json.unitSystems) && Array.isArray(json.units)) {
                    setBundle(json as UnitBundleFile);
                    setExportedAt(json.exportedAt ?? null);
                } else if (json && json.resourceType && Array.isArray(json.items)) {
                    setParsed(json as ExportFile);
                    setExportedAt(json.exportedAt ?? null);
                } else {
                    setParseError("Invalid export file format.");
                }
            } catch { setParseError("Could not parse file as JSON."); }
        };
        reader.readAsText(f);
    };

    const importList = async (
        label: string,
        list: unknown[],
        fn: (t: string, dto: unknown) => Promise<unknown>,
        acc: ImportResult[]
    ) => {
        for (const item of list) {
            const name = `${label}: ${(item as any).name ?? "(unknown)"}`;
            try {
                await fn(token, item);
                acc.push({ name, status: "success" });
            } catch (err) {
                logApiError(err);
                acc.push({ name, status: "error", message: getUserMessage(err) });
            }
        }
    };

    const handleImport = async () => {
        if (!parsed && !bundle) return;
        setImporting(true); setResults([]);
        const acc: ImportResult[] = [];
        try {
            if (bundle) {
                // Dependency order: dimensions and systems must exist before units.
                await importList("Dimension", bundle.unitDimensions, importUnitDimension, acc);
                await importList("System", bundle.unitSystems, importUnitSystem, acc);
                await importList("Unit", bundle.units, importUnit, acc);
            } else if (parsed) {
                const fn =
                    parsed.resourceType === "CATEGORY"      ? importCategory   :
                    parsed.resourceType === "UNIT"          ? importUnit       :
                    parsed.resourceType === "UNIT_SYSTEM"   ? importUnitSystem :
                                                              importUnitDimension;
                await importList(RESOURCE_LABELS[parsed.resourceType], parsed.items, fn, acc);
            }
        } finally {
            setResults(acc);
            setImporting(false);
        }
    };

    return (
        <div style={{ maxWidth: 480 }}>
            <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label>Select export file (.json)</label>
                <input type="file" accept=".json,application/json" onChange={handleFileChange} disabled={importing} />
            </div>

            {parseError && <div className="error-message" style={{ marginBottom: "0.75rem" }}>{parseError}</div>}

            {bundle && (
                <div style={{ padding: "0.75rem 1rem", background: "var(--bg-secondary)", borderRadius: "var(--radius-md)", marginBottom: "1rem", fontSize: "0.9rem" }}>
                    <strong>Unit Data Bundle</strong>
                    {" — "}{bundle.unitDimensions.length} dimension{bundle.unitDimensions.length !== 1 ? "s" : ""},
                    {" "}{bundle.unitSystems.length} system{bundle.unitSystems.length !== 1 ? "s" : ""},
                    {" "}{bundle.units.length} unit{bundle.units.length !== 1 ? "s" : ""}
                    {exportedAt && (
                        <span style={{ color: "var(--text-secondary)", marginLeft: "0.5rem" }}>
                            exported {new Date(exportedAt).toLocaleString()}
                        </span>
                    )}
                </div>
            )}

            {parsed && (
                <div style={{ padding: "0.75rem 1rem", background: "var(--bg-secondary)", borderRadius: "var(--radius-md)", marginBottom: "1rem", fontSize: "0.9rem" }}>
                    <strong>{RESOURCE_LABELS[parsed.resourceType]}</strong>
                    {" — "}{parsed.items.length} item{parsed.items.length !== 1 ? "s" : ""}
                    {exportedAt && (
                        <span style={{ color: "var(--text-secondary)", marginLeft: "0.5rem" }}>
                            exported {new Date(exportedAt).toLocaleString()}
                        </span>
                    )}
                </div>
            )}

            <button onClick={handleImport} disabled={(!parsed && !bundle) || importing}>
                {importing ? "Importing…" : "Import"}
            </button>

            {results.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                    {results.map((r, i) => (
                        <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "baseline", padding: "0.3rem 0", borderBottom: "1px solid var(--border-color)" }}>
                            <span style={{ color: r.status === "success" ? "var(--color-success, green)" : "var(--color-error, red)" }}>
                                {r.status === "success" ? "✓" : "✗"}
                            </span>
                            <span style={{ fontWeight: 500 }}>{r.name}</span>
                            {r.message && <span style={{ fontSize: "0.85rem", color: "var(--color-error, red)" }}>— {r.message}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImportExportPanel;
